import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import TableChartIcon from "@mui/icons-material/TableChart";

import { withTranslation } from "react-i18next";

import Dialog from "@plannel/components/Dialog";
import DataState from "@plannel/components/aggrid/DataState";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FpGridSetting from "@plannel/pages/factory-plan/common/grid";
import { AddButton, FullScreenButton, RemoveButton, SaveButton } from "@plannel/components/ActionIconButton";
import dateUtils from "@plannel/utils/date-util";

// 제외(MIG): old InputOrderBoard.jsx:33-48의 위젯 2개(WI_FP_SCHEDULEDINPUTPRODUCTIONORDER /
//   WI_FP_SCHEDULEDORDERTOCOMPLETEPRODUCTION)는 old에서 주석 처리된 채 시드에만 등록된 고아라
//   이관 대상이 아니다(fp-dashboard-screen-guide.md §0-8 "old대로 제외" 확정).
// 제외(MIG): old 위젯의 moveGrid2/setInputSeqByDate/moveUp/moveDown 4함수는 선언만 되고 UI 배선이
//   0건이었다(가운데 컬럼이 빈 Box). setInputSeqByDate는 정의되지 않은 dataProvider를 참조해 호출되는
//   순간 예외가 난다. moveGrid2(체크행 좌→우 이동)의 의도는 아래 그리드 간 드래그가 대신 커버한다.
// 제외(MIG): old DashboardPanel id가 5000 ProductionPerformance와 중복('Inputorderboard')이던 버그는
//   new에 레이아웃 저장 개념이 없어 자연 소멸한다(가이드 §0-8).

// TODO(MIG-T2): [inputorderboard/InputOrderBoard] 실데이터 연동 미이관(목업 유지).
//   old: 모듈 상단 columnData1/columnData2 하드코딩 배열을 fillJsonData()로 주입, API 호출 0건
//   사유: old도 목업 데모라 기능 후퇴가 없다 — "old와 동일하게 목업으로 이관" 확정(가이드 §0-8).
// old columnData1 그대로(6행). DUEDATE는 old가 new Date('2023-03-17')로 시각이 없는 값이었고
// RealGrid dataType:"datetime"은 로케일 날짜+시각으로 표시했으므로 ISO 문자열로 옮긴다(§C-4c).
const DELAYED_ORDERS = [
  { PRODUCTIONORDER: "PO2302100125", PRODUCT: "PD#4-고압전동기(4P, 2~1)", QTY: 6, INPUTPLAN: "3/6", DELAYDAYS: 11, DUEDATE: "2023-03-17T00:00:00" },
  { PRODUCTIONORDER: "PO2302152312", PRODUCT: "PD#15-초고압차단기(WIB)", QTY: 4, INPUTPLAN: "3/8", DELAYDAYS: 9, DUEDATE: "2023-03-20T00:00:00" },
  { PRODUCTIONORDER: "PO2302180056", PRODUCT: "PD#8-초고압변압기(15kW)", QTY: 8, INPUTPLAN: "3/8", DELAYDAYS: 9, DUEDATE: "2023-03-21T00:00:00" },
  { PRODUCTIONORDER: "PO2303021821", PRODUCT: "PD#5-고압전동기(4P, 2~1)", QTY: 2, INPUTPLAN: "3/9", DELAYDAYS: 8, DUEDATE: "2023-03-21T00:00:00" },
  { PRODUCTIONORDER: "PO2303022018", PRODUCT: "PD#4-고압전동기(4P, 2~1)", QTY: 3, INPUTPLAN: "3/13", DELAYDAYS: 4, DUEDATE: "2023-03-22T00:00:00" },
  { PRODUCTIONORDER: "PO2303030765", PRODUCT: "PD#4-고압전동기(4P, 2~1)", QTY: 3, INPUTPLAN: "3/13", DELAYDAYS: 4, DUEDATE: "2023-03-22T00:00:00" }
];

// old columnData2 그대로(12행). 이쪽은 old도 INPUTPLAN/DELAYDAYS 컬럼 자체가 없다.
const SCHEDULED_ORDERS = [
  { PRODUCTIONORDER: "PO2302210112", PRODUCT: "PD#5-고압전동기(4P, 2~1)", QTY: 4, DUEDATE: "2023-03-30T00:00:00" },
  { PRODUCTIONORDER: "PO2302210123", PRODUCT: "PD#5-고압전동기(4P, 2~1)", QTY: 4, DUEDATE: "2023-03-30T00:00:00" },
  { PRODUCTIONORDER: "PO2302210214", PRODUCT: "PD#2-저압전동기(800 * 140)", QTY: 20, DUEDATE: "2023-03-30T00:00:00" },
  { PRODUCTIONORDER: "PO2302210345", PRODUCT: "PD#2-저압전동기(800 * 140)", QTY: 14, DUEDATE: "2023-03-30T00:00:00" },
  { PRODUCTIONORDER: "PO2303060584", PRODUCT: "PD#4-고압전동기(4P, 2~1)", QTY: 3, DUEDATE: "2023-03-30T00:00:00" },
  { PRODUCTIONORDER: "PO2303080008", PRODUCT: "PD#4-고압전동기(4P, 2~1)", QTY: 6, DUEDATE: "2023-03-30T00:00:00" },
  { PRODUCTIONORDER: "PO2303080024", PRODUCT: "PD#4-고압전동기(4P, 2~1)", QTY: 2, DUEDATE: "2023-03-30T00:00:00" },
  { PRODUCTIONORDER: "PO2303080167", PRODUCT: "PD#8-초고압변압기(15kW)", QTY: 1, DUEDATE: "2023-04-03T00:00:00" },
  { PRODUCTIONORDER: "PO2303090021", PRODUCT: "PD#8-초고압변압기(15kW)", QTY: 1, DUEDATE: "2023-04-03T00:00:00" },
  { PRODUCTIONORDER: "PO2303090312", PRODUCT: "PD#8-초고압변압기(15kW)", QTY: 1, DUEDATE: "2023-04-03T00:00:00" },
  { PRODUCTIONORDER: "PO2303090335", PRODUCT: "PD#8-초고압변압기(15kW)", QTY: 1, DUEDATE: "2023-04-04T00:00:00" },
  { PRODUCTIONORDER: "PO2303090362", PRODUCT: "PD#11-초고압변압기(15kW)", QTY: 2, DUEDATE: "2023-04-04T00:00:00" }
];

// old 위젯 GridAddRowButton이 만들던 빈 행.
const EMPTY_ORDER_ROW = { PRODUCTIONORDER: "", PRODUCT: "", QTY: null, INPUTPLAN: "", DELAYDAYS: null, DROP_STATUS: null, DUEDATE: null };

// old setCellStyleCallback(:199-216)의 이동 표시 배경 2종.
const MOVE_MARK_REORDER = "#bbdddc";   // ① 같은 그리드 안에서 순서만 바꾼 행
const MOVE_MARK_CROSS = "#fec3c6";     // ② 다른 그리드에서 넘어온 행
// old grid1Items DELAYDAYS styleCallback의 강조색.
const DELAY_DAYS_COLOR = "#FE3333";

// 전체화면(카드 확대) 대상 식별자 — 대시보드 카테고리 공통 규칙(§C-4b)대로 카드 전부에 단다.
const CARD = { DELAYED: "delayedOrders", SCHEDULED: "scheduledOrders" };

// FpGridSetting의 centerAligned 컬럼타입이 넣는 정적 cellStyle과 같은 값. colDef.cellStyle이 있으면
// 타입 쪽 cellStyle은 통째로 덮이므로(§B-5) 콜백이 센터링을 직접 병합해서 돌려줘야 한다.
const CENTER_CELL_STYLE = { display: "flex", justifyContent: "center", alignItems: "center" };

// 오더번호 셀 = 센터정렬 + 이동 표시 배경.
// old는 이동 플래그를 DROP_STATUS(순번 컬럼)에 9999/1111로 덮어써 순번 자리에 그 숫자가 그대로
// 보이는 부작용이 있었다 → 마커를 별도 필드(moveMark)로 분리하고 순번은 항상 1..n을 유지한다
// (가이드 §0-8 "old 버그는 그대로 옮기지 말 것"). 판정은 rowIndex가 아니라 데이터 값 기준(§A-4).
const orderNoCellStyle = (params) => {
  const mark = params.data?.moveMark;
  if (mark === "REORDER") return { ...CENTER_CELL_STYLE, backgroundColor: MOVE_MARK_REORDER };
  if (mark === "CROSS") return { ...CENTER_CELL_STYLE, backgroundColor: MOVE_MARK_CROSS };
  return CENTER_CELL_STYLE;
};

// old grid1Items(7컬럼) — 헤더/타입/정렬/폭을 1:1로 옮긴다.
// 드래그 핸들 컬럼(`rowDrag: true`)은 두지 않는다 — old엔 핸들 아이콘이 없었고, 아래 gridOptions의
// `rowDragEntireRow`가 행 전체를 잡을 수 있게 해준다(§D-3).
const DELAYED_COLUMN_DEFS = [
  { headerName: "FP_PO", field: "PRODUCTIONORDER", width: 170, type: ["centerAligned"], cellStyle: orderNoCellStyle },
  { headerName: "PRODUCT", field: "PRODUCT", width: 240 },
  { headerName: "FP_DEF_QTY", field: "QTY", width: 90, type: ["numberColumn", "rightAligned"], editable: true },
  { headerName: "FP_INPUT_PLAN", field: "INPUTPLAN", width: 110, type: ["centerAligned"] },
  // old는 textAlignment:"far"(우측)였지만 styleCallback이 styleName "rg-near"를 돌려줘 실제 렌더는
  // 좌측정렬 빨강이었다 — "코드에 적힌 값"이 아니라 "실제로 렌더된 값" 기준으로 이관한다(§C-4a 계열).
  { headerName: "DAYS_LATE", field: "DELAYDAYS", width: 110, cellStyle: { color: DELAY_DAYS_COLOR } },
  { headerName: "seq", field: "DROP_STATUS", width: 80, type: ["rightAligned"] },
  { headerName: "FP_DUE_DT", field: "DUEDATE", width: 150, type: ["rightAligned"], valueFormatter: dateUtils.formatDateTime }
];

// old grid2Items(5컬럼). DUEDATE 정렬만 좌측 그리드와 다르다(old: far vs center).
const SCHEDULED_COLUMN_DEFS = [
  { headerName: "FP_PO", field: "PRODUCTIONORDER", width: 170, type: ["centerAligned"], cellStyle: orderNoCellStyle },
  { headerName: "PRODUCT", field: "PRODUCT", width: 240 },
  { headerName: "FP_DEF_QTY", field: "QTY", width: 90, type: ["numberColumn", "rightAligned"], editable: true },
  { headerName: "seq", field: "DROP_STATUS", width: 80, type: ["rightAligned"] },
  { headerName: "FP_DUE_DT", field: "DUEDATE", width: 150, type: ["centerAligned"], valueFormatter: dateUtils.formatDateTime }
];

// old setInputSeq1/2 — 로드/이동 후 보이는 순서대로 순번을 1..n으로 다시 매긴다.
// getRowId를 쓰지 않으므로 applyTransaction의 update는 데이터 객체 참조로 매칭된다(§0-4).
const renumberSeq = (api) => {
  if (!api || api.isDestroyed()) return;

  const rows = [];
  api.forEachNode((node, index) => {
    node.data.DROP_STATUS = index + 1;
    rows.push(node.data);
  });

  if (rows.length > 0) api.applyTransaction({ update: rows });
};

// old dataDropOptions.callback ① — 같은 그리드 안에서 순서만 바꾼 경우. 순서 자체는 rowDragManaged가
// 처리하고, 여기서는 old처럼 이동 표시를 남기고 순번을 다시 매긴다.
const handleRowDragEnd = (params) => {
  const api = params.api;
  const dragged = params.nodes ?? (params.node ? [params.node] : []);

  // 다른 그리드로 빠져나간 행(아래 크로스 드롭에서 이미 제거됨)까지 REORDER로 덮지 않도록,
  // 아직 이 그리드에 남아 있는 행만 표시한다.
  const present = new Set();
  api.forEachNode((node) => present.add(node.data));
  dragged.filter((node) => present.has(node.data)).forEach((node) => { node.data.moveMark = "REORDER"; });

  renumberSeq(api);
};

// old dataDropOptions.callback ② — 다른 그리드로 이동.
// ⚠️ 소스 제거도 타깃 추가도 **둘 다 직접 해야 한다.** AG Grid 공식 문서(row-dragging-to-grid)는
// 타깃이 drop zone을 통해 행을 "받는다"고 서술하지만, 이 화면 조합(양쪽 rowDragManaged + rowData
// prop)에서는 실제로 타깃에 추가되지 않는 것을 브라우저에서 확인했다 — 소스에서만 빠지고 타깃은
// 그대로였다(2026-08-13 사용자 확인). 자동 추가가 일어나는 구성일 수도 있으므로 데이터 객체
// 동일성으로 걸러 **중복 추가만 방지**한다(getRowId를 쓰지 않아 AG Grid도 참조로 매칭한다).
const createCrossDropHandler = (sourceApi, targetApi) => (params) => {
  // 다중행(rowDragMultiRow)이면 nodes, 단건이면 node — 위 handleRowDragEnd와 같은 폴백을 둔다.
  const dragged = params.nodes ?? (params.node ? [params.node] : []);
  if (dragged.length === 0) return;

  const movedData = dragged.map((node) => node.data);
  sourceApi.applyTransaction({ remove: movedData });

  const existing = new Set();
  targetApi.forEachNode((node) => existing.add(node.data));
  const pending = movedData.filter((data) => !existing.has(data));

  if (pending.length > 0) {
    // old는 놓은 자리(targetIndex)에 끼워 넣었다 — overIndex가 없으면(빈 영역에 드롭) 맨 뒤로 간다.
    const overIndex = params.overIndex;
    const addIndex = typeof overIndex === "number" && overIndex >= 0 ? overIndex : undefined;
    targetApi.applyTransaction({ add: pending, addIndex });
  }

  // "넘어온 행" 표시 — 위에서 타깃에 넣은 것과 같은 데이터 객체다.
  movedData.forEach((data) => { data.moveMark = "CROSS"; });

  renumberSeq(sourceApi);
  renumberSeq(targetApi);
};

const InputOrderBoard = ({ t, viewName, title }) => {
  // 마운트 후에는 그리드 API(applyTransaction/rowDragManaged)가 행의 원본이다 — saas-web 하우스
  // 패턴(MeasureLayoutModal/DemandPriority)과 동일. 다만 전체화면 전환이 그리드를 재마운트시키므로
  // (§C-4b) 그때 되살릴 최신 스냅샷을 ref로 들고 있는다. state로 두면 매 조작마다 rowData prop이
  // 갈리면서 드래그 중인 그리드와 충돌한다.
  const delayedRowsRef = useRef(DELAYED_ORDERS.map((row, index) => ({ ...row, DROP_STATUS: index + 1 })));
  const scheduledRowsRef = useRef(SCHEDULED_ORDERS.map((row, index) => ({ ...row, DROP_STATUS: index + 1 })));

  const delayedGridRef = useRef();
  const scheduledGridRef = useRef();
  const [delayedApi, setDelayedApi] = useState(null);
  const [scheduledApi, setScheduledApi] = useState(null);

  // 전체화면 — 공용 FullScreenButton 계약대로 boolean + 대상 id 2개.
  const [fullScreen, setFullScreen] = useState(false);
  const [fullScreenCardId, setFullScreenCardId] = useState(null);

  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  // 재마운트로 날아가는 컬럼 순서/폭/정렬 보존(§C-4b). 그리드가 2개라 보관함도 2벌이다.
  const delayedColumnStateRef = useRef(null);
  const scheduledColumnStateRef = useRef(null);

  // 전체화면 전환 직전에 두 그리드의 컬럼 상태와 행 스냅샷을 함께 챙긴다.
  const syncGridState = () => {
    [
      [delayedGridRef, delayedColumnStateRef, delayedRowsRef],
      [scheduledGridRef, scheduledColumnStateRef, scheduledRowsRef]
    ].forEach(([gridRef, columnStateRef, rowsRef]) => {
      const api = gridRef.current?.api;
      if (!api || api.isDestroyed()) return;

      api.stopEditing();
      columnStateRef.current = api.getColumnState();

      const rows = [];
      api.forEachNode((node) => rows.push(node.data));
      rowsRef.current = rows;
    });
  };

  const defaultGridMemo = useMemo(() => {
    const settings = FpGridSetting({ title, viewName });
    return {
      ...settings,
      // TODO(MIG-T2): [inputorderboard/InputOrderBoard] 정렬/필터 적용 중 "그리드 내" 순서 변경 비활성.
      //   old: RealGrid는 컬럼 정렬과 dataDropOptions(행 이동)가 동시에 동작했다
      //   사유: AG Grid managed 드래그의 구조적 제약 — `RowDragService.computeVisibility()`가
      //        정렬/필터가 걸려 있으면 "disabled"를 돌려주고(ag-grid-community 35 main.cjs.js:23491-23495)
      //        `newRowsDrop`이 `allowed=false`로 managed 이동을 막는다(:23021). 정렬을 해제하면 살아난다.
      //        둘을 동시에 쓰려면 unmanaged 드래그로 재작성해야 한다(2차 과제).
      //   ※ 범위 주의: **그리드 간 이동은 정렬 중에도 정상 동작한다**(2026-08-13 브라우저 확인).
      //     managed 게이트는 `sameGrid` 경로에만 걸리고, 그리드 간 이동은 아래 createCrossDropHandler가
      //     applyTransaction으로 직접 처리하기 때문이다. 제약을 이보다 넓게 적지 말 것.
      defaultColDef: {
        ...settings.defaultColDef,
        // old grid1Items/grid2Items는 QTY만 editable:true — 나머지는 전부 읽기 전용이다.
        editable: false
        // 정렬은 old대로 켜 둔다(사용자마다 보는 정렬이 다름 — 2026-08-13 사용자 확인).
        // DefaultGridSetting의 sortable:true를 그대로 쓴다.
      },
      // old editOptions.movable + dataDropOptions.dropMode:'move' 대응(그리드 내 순서 변경).
      // saas-web 하우스 패턴(MeasureLayoutModal/DemandPriority/MpSettings)과 동일 조합.
      rowDragManaged: true,
      animateRows: true,
      rowDragMultiRow: true,
      // old displayOptions.selectAndImmediateDrag 대응 — 핸들이 아니라 **행 아무 곳이나** 잡아도
      // 바로 끌린다.
      // ⚠️ 이 옵션은 **셀 선택(범위 선택)이 켜져 있으면 조용히 무시된다**: RowComp.addRowDraggerToRow가
      //    RowDragService.createRowDragCompForRow를 부르는데, 그쪽이 `_isCellSelectionEnabled(gos)`면
      //    undefined를 돌려주고 호출부가 그대로 return한다(ag-grid-community 35 main.cjs.js:23438, :35965).
      //    공용 DefaultGridSetting은 cellSelection:true라 아래 한 줄이 없으면 rowDragEntireRow가
      //    무동작이 된다 — 짝으로만 의미가 있으니 둘을 떼어놓지 말 것.
      rowDragEntireRow: true,
      // 범위 선택은 old RealGrid에도 없었고 Tier 규칙상 T2(범위선택/클립보드)다. 셀 포커스
      // (getFocusedCell — 행추가 위치 기준)와 셀 편집은 cellSelection과 별개라 그대로 동작한다.
      cellSelection: false,
      // 위 둘로 행 전체가 드래그되므로 **컬럼에 `rowDrag`를 두지 않는다** — old엔 핸들 아이콘이
      // 없었다(2026-08-13 브라우저 확인 후 제거).
      // old setVisibleProps(grid, true, true, true)의 체크바 대응 + 다중행 드래그 선택용.
      rowSelection: { ...settings.rowSelection, checkboxes: true, headerCheckbox: true, enableClickSelection: false }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 정적 컬럼이지만 onGridReady 1회가 아니라 effect로 태운다 — 언어 전환(t) 시 헤더 재번역 +
  // 전체화면 전환으로 그리드가 재마운트될 때 새 api에 컬럼 재적용(§C-4b).
  useEffect(() => {
    // isDestroyed 가드 필수 — 전체화면으로 "다른" 카드를 띄우면 이 그리드는 언마운트되지만 api state는
    // 파기된 인스턴스를 그대로 들고 있다. 그 상태에서 언어를 전환하면(deps의 t) 파기된 api에 컬럼을
    // 걸려다 터진다.
    if (!delayedApi || delayedApi.isDestroyed()) return;
    GridUtils.setColumnDefs({ api: delayedApi, columnDefs: DELAYED_COLUMN_DEFS, viewName, gridId: "delayedOrderGrid", initState: false });
  }, [delayedApi, viewName, t]);

  useEffect(() => {
    if (!scheduledApi || scheduledApi.isDestroyed()) return;
    GridUtils.setColumnDefs({ api: scheduledApi, columnDefs: SCHEDULED_COLUMN_DEFS, viewName, gridId: "scheduledOrderGrid", initState: false });
  }, [scheduledApi, viewName, t]);

  // 재마운트 직후 1회만 컬럼 상태를 되돌린다. deps를 [api]로 좁힌 것이 핵심 — 위 effect와 합치거나
  // t를 deps에 넣으면 언어를 전환할 때 낡은 상태가 현재 순서를 덮어쓴다(§C-4b).
  useEffect(() => {
    if (!delayedApi || delayedApi.isDestroyed() || !delayedColumnStateRef.current) return;
    delayedApi.applyColumnState({ state: delayedColumnStateRef.current, applyOrder: true });
  }, [delayedApi]);

  useEffect(() => {
    if (!scheduledApi || scheduledApi.isDestroyed() || !scheduledColumnStateRef.current) return;
    scheduledApi.applyColumnState({ state: scheduledColumnStateRef.current, applyOrder: true });
  }, [scheduledApi]);

  // 그리드 간 행 드래그(AG Grid Community RowDragModule) — 서로의 drop zone을 상대에게 등록한다.
  // 전체화면일 때는 한쪽 그리드만 마운트돼 반대쪽 api가 이미 파기된 상태라 등록을 건너뛴다
  // (그 상태에선 애초에 끌어다 놓을 상대 그리드가 화면에 없다).
  useEffect(() => {
    if (!delayedApi || !scheduledApi || delayedApi.isDestroyed() || scheduledApi.isDestroyed()) return;

    const dropOnScheduled = scheduledApi.getRowDropZoneParams({ onDragStop: createCrossDropHandler(delayedApi, scheduledApi) });
    const dropOnDelayed = delayedApi.getRowDropZoneParams({ onDragStop: createCrossDropHandler(scheduledApi, delayedApi) });

    delayedApi.addRowDropZone(dropOnScheduled);
    scheduledApi.addRowDropZone(dropOnDelayed);

    return () => {
      if (!delayedApi.isDestroyed()) delayedApi.removeRowDropZone(dropOnScheduled);
      if (!scheduledApi.isDestroyed()) scheduledApi.removeRowDropZone(dropOnDelayed);
    };
  }, [delayedApi, scheduledApi]);

  // old GridAddRowButton + onAfterAdd(gridView.getCurrent() 기준) — 그리드에 빈 행 1건을 넣고
  // 순번을 다시 매긴다. 보낼 백엔드가 없으므로 그리드 로컬 추가까지만이다.
  const addOrderRow = (gridRef) => {
    const api = gridRef.current?.api;
    if (!api) return;

    api.stopEditing();

    // 삽입 위치는 **포커스된 셀의 바로 아래**다(old의 "현재 행" 기준과 같은 개념).
    // GridUtils.addRow는 addIndex를 안 주면 체크박스 선택행 아래(선택이 없으면 맨 위)로 넣는데,
    // 이 화면은 체크박스가 다중행 드래그 선택용이라 삽입 위치까지 좌우하면 안 된다
    // (2026-08-13 사용자 지시). 포커스가 없을 때만 GridUtils 기본 동작으로 떨어진다.
    const focusedCell = api.getFocusedCell();
    const addIndex = focusedCell ? focusedCell.rowIndex + 1 : 0;

    const createdNode = GridUtils.addRow(api, { ...EMPTY_ORDER_ROW }, addIndex);
    DataState.setDataState(createdNode);
    renumberSeq(api);

    // 연속으로 누르면 아래로 쌓이도록 포커스를 새 행으로 옮긴다(안 옮기면 같은 자리에 계속 끼어든다).
    // DataState.setDataState가 포커스를 첫 컬럼으로 되돌리므로 반드시 그 뒤에 호출한다.
    if (focusedCell) api.setFocusedCell(createdNode.rowIndex, focusedCell.column);
  };

  // TODO(MIG-T2): [inputorderboard/InputOrderBoard] 저장/삭제 실동작 + 완료 메시지 미이관.
  //   old: deleteLeftLine/deleteRightLine/saveLeftLine/saveRightLine — 확인창을 띄운 뒤
  //        transLangKey('Success deletion left data')처럼 랭키가 아닌 생 영문만 표시하고 실제
  //        삭제/저장은 하지 않았다(API 호출 0건)
  //   사유: 백엔드 엔드포인트가 없고 old도 무동작이었다. 그 문자열은 랭키가 아니라 그대로 옮기면
  //        하드코딩 영문이 된다 — 확인 다이얼로그까지만 이관한다(2026-08-13 사용자 확정).
  // TODO(MIG-T2): [inputorderboard/InputOrderBoard] 삭제 시 체크행 0건 안내 분기 미이관.
  //   old: 체크행이 없으면 MSG_SELECT_DELETE 안내, 있으면 확인창 진행
  //   사유: 실제 삭제가 없어 분기가 무의미하다.
  const handleDialog = () => {
    // 확인/취소 모두 아무 동작도 하지 않는다(위 유보 사유 참고).
  };

  // TODO(MIG-T2): [inputorderboard/InputOrderBoard] 행번호(rowIndicator)·상태바(stateBar) 거터 미이관.
  //   old: setVisibleProps(grid, true, true, true) — 체크바 외에 행번호/행 변경상태 거터도 표시
  //   사유: AG Grid에 대응 컬럼이 없어 새 컬럼을 만들면 old 컬럼 수와 어긋난다. 행번호는 순번(Seq)
  //        컬럼이, 변경상태는 DataState 행 배경색이 각각 대신한다.
  // 카드 헤더 45px — 제목 가운데 정렬을 유지해야 해서 버튼군만 absolute로 띄운다(§A-2/§C-4b).
  const cardHeader = (label, cardId, gridRef) => (
    <Box sx={{ position: "relative", display: "flex", height: "45px", alignItems: "flex-end", justifyContent: "center" }}>
      <TableChartIcon fontSize={"medium"} sx={{ mb: 0.2 }} />
      <Typography className="chart-title" variant="h6" sx={{ pl: 1, textAlign: "center", fontWeight: 500 }}>
        {label}
      </Typography>
      <Box sx={{ position: "absolute", right: 0, mr: 0.5, display: "flex" }}>
        <AddButton onClick={() => addOrderRow(gridRef)} />
        <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
        <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
        {/* setter를 그대로 넘기지 않고 컬럼 상태·행 스냅샷 캡처를 얹은 래퍼를 넘긴다 — 공용
            FullScreenButton의 props 계약 {fullScreen,setFullScreen,screenId,setScreenId}는 그대로. */}
        <FullScreenButton
          fullScreen={fullScreen && fullScreenCardId === cardId}
          setFullScreen={(next) => { syncGridState(); setFullScreen(next); }}
          screenId={cardId}
          setScreenId={(next) => { syncGridState(); setFullScreenCardId(next); }}
        />
      </Box>
    </Box>
  );

  // 카드 1개를 그리는 함수 — 일반 배치와 전체화면(카드 하나만 100%) 양쪽에서 같은 정의를 쓴다(§C-4b).
  // ⚠️ `key={cardId}`가 필수다. 이 화면은 일반 배치와 전체화면 **양쪽 모두 최상위 자식이 <Card>**라
  //    (다른 대시보드는 일반 배치 최상위가 <Box> 행 컨테이너여서 타입이 달랐다) key가 없으면 React가
  //    index로 매칭해 **엉뚱한 카드의 fiber를 재사용**한다 — 우측 카드를 전체화면으로 띄우면 살아남는
  //    인스턴스가 좌측 그리드라 onGridReady가 재발화하지 않고, scheduledApi는 파기된 api를 든 채여서
  //    좌측 컬럼 정의(7개)가 우측 데이터에 적용됐다. key를 주면 React가 key로 매칭해 해당 카드의
  //    fiber를 그대로 살리고 반대쪽만 언마운트한다.
  const renderCard = (cardId, sx) => {
    const isDelayed = cardId === CARD.DELAYED;
    const gridRef = isDelayed ? delayedGridRef : scheduledGridRef;

    return (
      <Card key={cardId} sx={sx}>
        {cardHeader(isDelayed ? t("UI_SA_FP_INPUT_DELAYED_PO") : t("UI_SA_FP_INPUT_PO"), cardId, gridRef)}
        <Box className="ag-theme-balham" width={"100%"} height={"calc(100% - 45px)"}>
          <AgGridReact
            ref={gridRef}
            rowData={isDelayed ? delayedRowsRef.current : scheduledRowsRef.current}
            {...defaultGridMemo}
            onRowDragEnd={handleRowDragEnd}
            onGridReady={(params) => (isDelayed ? setDelayedApi(params.api) : setScheduledApi(params.api))}
          />
        </Box>
      </Card>
    );
  };

  // old 레이아웃(위젯 1개가 전폭, 내부는 flex 10 : 1 : 10의 좌우 2그리드)을 좌우 2카드로 옮긴다 —
  // 가운데 빈 스페이서는 별도 요소를 만들지 않고 카드 사이 여백(mr)으로 흡수한다(§B-3).
  return (
    <Box className={"grid-area"} sx={{ display: "flex", flexDirection: "column", width: "100%", height: "100%", minHeight: 560 }}>
      {fullScreen && fullScreenCardId ? (
        <Box sx={{ display: "flex", width: "100%", flex: 1, minHeight: 0 }}>
          {renderCard(fullScreenCardId, { width: "100%", height: "100%" })}
        </Box>
      ) : (
        <Box sx={{ display: "flex", width: "100%", flex: 1, minHeight: 0 }}>
          {renderCard(CARD.DELAYED, { width: "calc(50% - 6px)", height: "100%", mr: 1.5 })}
          {renderCard(CARD.SCHEDULED, { width: "50%", height: "100%" })}
        </Box>
      )}
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
    </Box>
  );
};

export default withTranslation()(InputOrderBoard);
